import { useState, useEffect, useCallback } from 'react';
import { toast } from '../utils/toast';
import {
  normalizeItineraryData,
  extractGuestInfo,
  safeJsonParse,
  transformItineraryData,
} from '../utils/itineraryDataProcessor';
import { fillMissingDayDates } from '../utils/dateUtils';

/**
 * Shared hook for itinerary data fetching and management
 * Used by all itinerary view components to ensure consistent functionality
 */
const useItineraryData = (id) => {
  // Core state
  const [itinerary, setItinerary] = useState(null);
  const [days, setDays] = useState([]);
  const [tripName, setTripName] = useState("");
  const [itineraryId, setItineraryId] = useState(null);
  const [tripDetails, setTripDetails] = useState({
    guestName: "",
    resort: "",
    startDate: "",
    endDate: "",
    generatedDate: "",
  });
  const [magicMessages, setMagicMessages] = useState({
    arrival: "Welcome to Disney!",
    departure: "Safe travels!",
  });
  const [segmentsV2, setSegmentsV2] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Apply API-shaped payload (raw from GET or regenerate POST response) to state
  const applyPayloadToState = useCallback((raw) => {
    if (!raw) return;
    const data = raw?.data || raw || {};
    let parsed = {};
    if (data.itinerary && typeof data.itinerary === "object") parsed = data.itinerary;
    else if (typeof data.ItineraryData === "string") parsed = safeJsonParse(data.ItineraryData) || {};
    else if (typeof data.ItineraryData === "object") parsed = data.ItineraryData || {};
    else if (raw.itinerary && typeof raw.itinerary === "object") parsed = raw.itinerary;
    else if (raw.ItineraryData) parsed = typeof raw.ItineraryData === "string" ? safeJsonParse(raw.ItineraryData) || {} : raw.ItineraryData || {};
    const normalized = normalizeItineraryData(parsed);
    let d = normalized?.days || normalized?.itinerary || normalized?.DailyItineraries || [];
    if (!d.length) d = raw?.days || raw?.data?.days || [];
    const transformedDays = transformItineraryData(Array.isArray(d) ? d : []);
    const gi = extractGuestInfo(normalized, normalized?.tripSummary);
    const startDateStr = gi?.startDate || data?.trip_start_date || data?.startDate || '';
    const daysToSet = fillMissingDayDates(transformedDays, startDateStr);
    setDays(daysToSet);
    setTripName(data?.TripName || raw?.tripname || `${gi?.guestName || "Guest"}'s Disney Trip`);
    setTripDetails({
      guestName: gi?.guestName || "Guest",
      resort: gi?.resort || gi?.hotel || "",
      startDate: gi?.startDate || data?.trip_start_date || data?.startDate || "",
      endDate: gi?.endDate || data?.trip_end_date || data?.endDate || "",
      generatedDate: raw?.createdat || data?.createdat || "",
    });
    setMagicMessages({
      arrival: raw?.arrivalmessage || parsed?.magicMessages?.arrival || "Welcome to Disney!",
      departure: raw?.departuremessage || parsed?.magicMessages?.departure || "Safe travels!",
    });
    setItineraryId(data?.TripId || raw?.itineraryid || id);
    setItinerary(normalized);
    const sv2 = raw?.data?.segmentsV2 ?? raw?.segmentsV2 ?? null;
    setSegmentsV2(sv2 && Array.isArray(sv2?.days) && sv2.days.length > 0 ? sv2 : null);
  }, [id]);

  // Shared fetch logic: can be called on mount or after regenerate (refetch with cache-bust)
  const fetchItinerary = useCallback(async (opts = {}) => {
    if (!id) return;
    const { cacheBuster = new URLSearchParams(window.location.search).get('_t') || Date.now(), silent = false, preloaded = null } = opts;
    // Show loading (full-page skeleton) so user sees visible refresh like before; then apply preloaded if any
    setLoading(true);
    setError("");
    if (preloaded && preloaded?.data?.itinerary) {
      applyPayloadToState(preloaded);
    }
    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/api/itineraries/${id}?_t=${cacheBuster}`,
        { credentials: "include", cache: 'no-cache' }
      );
      if (!res.ok) {
        if (res.status === 404) throw new Error(`Itinerary not found. The itinerary may not exist or you may not have access to it.`);
        if (res.status === 403) throw new Error(`You don't have permission to view this itinerary.`);
        throw new Error(`Failed to load itinerary (${res.status})`);
      }
      const raw = await res.json();
      applyPayloadToState(raw);
    } catch (e) {
      const errorMessage = e.message || "Failed to load itinerary";
      setError(errorMessage);
      console.error("Error loading itinerary:", e);
      if (!silent) toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [id, applyPayloadToState]);

  useEffect(() => {
    fetchItinerary();
  }, [fetchItinerary]);

  return {
    itinerary,
    days,
    segmentsV2,
    tripName,
    setTripName,
    itineraryId,
    tripDetails,
    magicMessages,
    loading,
    error,
    refetch: (opts) => fetchItinerary({ cacheBuster: Date.now(), silent: true, ...opts }),
  };
};

export default useItineraryData;
